import type { OnInit } from '@angular/core';
import { Component, Input } from '@angular/core';
import { unpackJSSRadioItem } from '@innogy/common-ui/forms';
import type { TrackedToolComponent } from '@analytics/models';
import { TrackToolService } from '@analytics/events';
import { componentViewModelSelector } from '@core/experience-editor-utils';
import { Store } from '@ngrx/store';
import type { SolarPanelCriteria } from '@innogy/feh/models';

import type { SolarPanelSuitabilityCheckToolTrackingSteps } from '../../store';
import {
  asbestosControlId,
  parsedSolarPanelsSuitabilityCheckToolTrackingConfig,
  renewedRoofWithinTimeLimitControlId,
  roofTypeControlId,
  selectSolarPanelsSuitabilityCheckIsInvalid,
  selectSolarPanelsSuitabilityCheckViewModel,
  solarPanelsSuitabilityCheckFormId,
  solarPanelsSuitabilityCheckFormSubmitAction,
} from '../../store';
import { SolarPanelsSuitabilityCheckRendering } from '../../models/solar-panels-suitability-check-rendering.interface';
import { initializeSolarPanelsFunnelComponent } from '../../utils/initialize-solar-panels-funnel-component';
import { mockSolarPanelsSuitabilityCheckViewModel } from './solar-panels-suitability-check-container.xp.mock';

@Component({
  selector: 'wl-solar-panels-suitability-check-container[rendering]',
  templateUrl: './solar-panels-suitability-check-container.component.html',
})
export class SolarPanelsSuitabilityCheckContainerComponent
  implements
    TrackedToolComponent<SolarPanelSuitabilityCheckToolTrackingSteps>,
    OnInit
{
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() rendering!: SolarPanelsSuitabilityCheckRendering;

  protected vmSelector = componentViewModelSelector(
    selectSolarPanelsSuitabilityCheckViewModel,
    mockSolarPanelsSuitabilityCheckViewModel
  );

  readonly viewModel$ = this.store$.select(this.vmSelector);
  readonly formIsInvalid$ = this.store$.select(
    selectSolarPanelsSuitabilityCheckIsInvalid
  );

  solarPanelsSuitabilityCheckFormId = solarPanelsSuitabilityCheckFormId;

  constructor(
    protected readonly store$: Store,
    private readonly trackToolService: TrackToolService
  ) {}

  ngOnInit() {
    this.store$.dispatch(
      initializeSolarPanelsFunnelComponent(
        'suitabilityCheck',
        this.rendering?.fields.FunnelSettings
      )
    );
  }

  get trackToolConfig() {
    const toolName = this.rendering.fields.componentName.value;
    return parsedSolarPanelsSuitabilityCheckToolTrackingConfig(toolName);
  }

  get asbestosOptions() {
    return this.rendering.fields.AsbestosRadios.map((item) =>
      unpackJSSRadioItem(item, asbestosControlId)
    );
  }

  get roofTypeOptions() {
    return this.rendering.fields.RoofTypeRadios.map((item) =>
      unpackJSSRadioItem(item, roofTypeControlId)
    );
  }

  get roofReplacedOptions() {
    return this.rendering.fields.RoofReplacedRadios.map((item) =>
      unpackJSSRadioItem(item, renewedRoofWithinTimeLimitControlId)
    );
  }

  roofMightBeSuitable(criteria: SolarPanelCriteria | undefined) {
    return criteria?.hasSufficientRoofArea && !criteria?.isMonument;
  }

  submit(event: Event, formIsValid: boolean) {
    event.preventDefault();

    this.store$.dispatch(solarPanelsSuitabilityCheckFormSubmitAction());
    if (formIsValid) {
      this.trackToolService.completeTool(this.trackToolConfig);
    }
  }
}
